// Minimal resilient SoundManager - provides named exports and default.
// This file is the first module resolved by imports without extension.

const SOUND_STATE_EVENT = 'reacta:sound-state-change';
const DEFAULT_MASTER_VOLUME = 30;

let soundEnabled = true;
let masterVolume = DEFAULT_MASTER_VOLUME;
let audioContext = null;

function getAudioContext() {
  if (typeof window === 'undefined') return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!audioContext || audioContext.state === 'closed') {
    audioContext = new Ctx();
  }
  return audioContext;
}

export function unlockAudio() {
  try {
    const ctx = getAudioContext();
    if (ctx?.state === 'suspended') {
      void ctx.resume();
    }
  } catch (err) {
    // Browser without an available audio device: keep the UI operational.
  }
}

if (typeof window !== 'undefined') {
  // Legacy persistent settings are intentionally retired: every new execution
  // starts audible and only a current-session user action may mute the system.
  try {
    window.localStorage.removeItem('buttonSoundEnabled');
    window.localStorage.removeItem('soundMasterVolume');
  } catch (err) {
    // Storage may be blocked without affecting session-only audio state.
  }

  const unlockOnFirstGesture = () => unlockAudio();
  window.addEventListener('pointerdown', unlockOnFirstGesture, { capture: true, once: true });
  window.addEventListener('keydown', unlockOnFirstGesture, { capture: true, once: true });
}

export function isSoundEnabled() {
  return soundEnabled;
}

export function setSoundEnabled(enabled) {
  soundEnabled = Boolean(enabled);
  if (soundEnabled) {
    unlockAudio();
  }
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SOUND_STATE_EVENT, {
      detail: { enabled: soundEnabled, volume: masterVolume },
    }));
  }
}

export function getMasterVolume() {
  return masterVolume;
}

export function setMasterVolume(volume) {
  masterVolume = Math.min(Math.max(Number(volume) || 0, 0), 100);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(SOUND_STATE_EVENT, {
      detail: { enabled: soundEnabled, volume: masterVolume },
    }));
  }
}

export function subscribeToSoundState(listener) {
  if (typeof window === 'undefined') return () => {};
  const handleChange = (event) => listener(event.detail);
  window.addEventListener(SOUND_STATE_EVENT, handleChange);
  return () => window.removeEventListener(SOUND_STATE_EVENT, handleChange);
}

function getSoundProfile(name) {
  switch (name) {
    case 'boot':
    case 'boot_up':
    case 'sweep_down':
      return { frequency: 600, endFrequency: 200, gain: 0.12, duration: 0.5, type: 'sine' };
    case 'door_open':
    case 'door':
      return { frequency: 400, endFrequency: 260, gain: 0.1, duration: 0.35, type: 'triangle' };
    case 'button':
    case 'short_beep':
      return { frequency: 880, endFrequency: 880, gain: 0.08, duration: 0.08, type: 'square' };
    case 'success':
    case 'all_clear':
      return { frequency: 660, endFrequency: 990, gain: 0.1, duration: 0.32, type: 'sine' };
    case 'error':
      return { frequency: 160, endFrequency: 80, gain: 0.14, duration: 0.38, type: 'sawtooth' };
    case 'biohazard_alert':
      return { frequency: 420, endFrequency: 250, gain: 0.18, duration: 0.75, type: 'sawtooth' };
    case 'radiological_alert':
      return { frequency: 760, endFrequency: 380, gain: 0.16, duration: 0.65, type: 'triangle' };
    case 'terror_alert':
    case 'tactical_lockdown':
      return { frequency: 110, endFrequency: 55, gain: 0.22, duration: 1.05, type: 'sawtooth' };
    case 'major_incident':
      return { frequency: 520, endFrequency: 520, gain: 0.14, duration: 0.45, type: 'triangle' };
    case 'activate':
    default:
      return { frequency: 520, endFrequency: 700, gain: 0.1, duration: 0.2, type: 'sine' };
  }
}

export function playProceduralSound(name, opts = {}) {
  try {
    if (typeof window === 'undefined') return;
    if (!isSoundEnabled() && !opts.force) return;

    const ctx = getAudioContext();
    if (!ctx) return;
    if (ctx.state === 'suspended') {
      void ctx.resume();
    }
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const profile = getSoundProfile(name);
    const duration = opts.duration || profile.duration;
    const masterVolume = getMasterVolume() / 100;
    const requestedVolume = opts.volume ?? 1;
    const gainValue = profile.gain * masterVolume * requestedVolume;

    osc.type = opts.type || profile.type;
    osc.frequency.setValueAtTime(profile.frequency, ctx.currentTime);
    osc.frequency.linearRampToValueAtTime(profile.endFrequency, ctx.currentTime + duration);
    gain.gain.setValueAtTime(Math.max(gainValue, 0.0001), ctx.currentTime);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);

    setTimeout(() => {
      try {
        osc.stop();
      } catch (err) {
        // silent fallback
      }
    }, duration * 1000 + 50);
  } catch (err) {
    // silent fallback
  }
}

export function playUISound(kind = 'button') {
  if (kind === 'beep') return playProceduralSound('button', { duration: 0.08 });
  if (kind === 'activate') return playProceduralSound('activate', { duration: 0.2 });
  return playProceduralSound(kind, { duration: 0.12 });
}

export default {
  getMasterVolume,
  isSoundEnabled,
  playProceduralSound,
  playUISound,
  setMasterVolume,
  setSoundEnabled,
  subscribeToSoundState,
  unlockAudio,
};
